import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { skip, switchMap, tap, shareReplay } from 'rxjs/operators';
import { FormConfigurationService } from '../../proxy/configurations/form-configuration.service';
import { FormConfigurationDto } from '../../proxy/configurations/form-configurations/models';
import type { PagedResultDto } from '@abp/ng.core';

/**
 * Cache service for FormConfiguration list.
 * 
 * Wraps the ABP proxy FormConfigurationService with caching.
 * Components should still use FormConfigurationService for CRUD operations,
 * but call refresh() on this service after any create/update/delete.
 * 
 * Features:
 * - Loads configuration list once and caches
 * - Manual refresh after CRUD operations
 * - Quick lookup by template ID or configuration ID
 * - Helper methods for resolving title fields
 */
@Injectable({ providedIn: 'root' })
export class FormConfigurationCacheService {
  private readonly reload$ = new BehaviorSubject<void>(undefined);
  private readonly configurations$ = this.reload$.pipe(
    switchMap(() => this.fetchAll()),
    shareReplay(1)
  );

  // Indexes for quick lookup
  private byTemplate: Map<string, FormConfigurationDto[]> = new Map();
  private byConfigurationId: Map<string, FormConfigurationDto> = new Map();
  private cachedItems: FormConfigurationDto[] = [];

  constructor(private readonly configurationService: FormConfigurationService) {}

  /** Public observable for consumers */
  getAll$(): Observable<PagedResultDto<FormConfigurationDto>> {
    return this.configurations$;
  }

  /** Force refresh of cache - call after any CRUD operation */
  refresh(): void {
    this.reload$.next();
  }

  /**
   * Refresh and resolve once the fresh value has landed in the cache.
   * Callers that need to read the cache immediately after a CRUD operation
   * should await this instead of the fire-and-forget refresh(), otherwise
   * they may observe the stale buffered value from shareReplay(1).
   */
  async refreshAsync(): Promise<PagedResultDto<FormConfigurationDto>> {
    // Subscribe before triggering reload so shareReplay is active when next()
    // fires, and skip the buffered (stale) replay to take the fresh emission.
    // Assumes init()/getAll$() has been called at least once so a buffer exists.
    const fresh = firstValueFrom(this.configurations$.pipe(skip(1)));
    this.reload$.next();
    return fresh;
  }

  /** Initialize cache (call on app startup) */
  async init(): Promise<void> {
    await firstValueFrom(this.configurations$);
  }

  /** Get cached items synchronously */
  getCachedItems(): FormConfigurationDto[] {
    return this.cachedItems;
  }

  /** Get all configurations for a template id (sync lookup after first load). */
  getByTemplateId(templateId: string): FormConfigurationDto[] {
    return this.byTemplate.get(templateId) || [];
  }

  /** Direct configuration fetch from cache (undefined if not loaded yet or doesn't have formFields). */
  getConfigurationById(configurationId: string): FormConfigurationDto | undefined {
    const cached = this.byConfigurationId.get(configurationId);
    // Only return if it has formFields (full data)
    if (cached && cached.formFields && cached.formFields.length > 0) {
      return cached;
    }
    return undefined;
  }

  /** 
   * Get configuration by ID synchronously (may not have formFields).
   * Use this only when you don't need formFields.
   */
  getConfigurationByIdBasic(configurationId: string): FormConfigurationDto | undefined {
    return this.byConfigurationId.get(configurationId);
  }

  /**
   * Get configuration by ID - returns from cache if available with formFields, otherwise fetches from API.
   * This replaces direct calls to configurationService.get()
   * 
   * NOTE: The list API may not return formFields, so we check if formFields exist before returning cached data.
   */
  get(configurationId: string): Observable<FormConfigurationDto> {
    const cached = this.byConfigurationId.get(configurationId);
    // Only use cache if it has formFields (the list API may not return them)
    if (cached && cached.formFields && cached.formFields.length > 0) {
      return new Observable(subscriber => {
        subscriber.next(cached);
        subscriber.complete();
      });
    }
    // Fetch from API and cache the full result (with formFields)
    return this.configurationService.get(configurationId).pipe(
      tap(cfg => {
        if (cfg?.id) {
          // Replace the cached item with the full version that has formFields
          this.byConfigurationId.set(cfg.id, cfg);
          // Also update byTemplate index
          const arr = this.byTemplate.get(cfg.formTemplateId) || [];
          const existingIndex = arr.findIndex(c => c.id === cfg.id);
          if (existingIndex >= 0) {
            arr[existingIndex] = cfg; // Replace with full version
          } else {
            arr.push(cfg);
          }
          this.byTemplate.set(cfg.formTemplateId, arr);
        }
      })
    );
  }

  /** Resolve templateId for a configuration id (first match). */
  getTemplateIdByConfigurationId(configurationId: string): string | undefined {
    for (const [templateId, cfgs] of this.byTemplate.entries()) {
      if (cfgs.some(c => c.id === configurationId)) return templateId;
    }
    return undefined;
  }

  /**
   * Try to resolve the Title field's formFieldDetailsId for a given template.
   */
  getTitleFieldIdForTemplate(templateId: string): string | undefined {
    const configs = this.getByTemplateId(templateId);
    for (const cfg of configs) {
      const field = cfg.formFields?.find(f => f.label === 'Title');
      if (field) return field.id;
    }
    return undefined;
  }

  /** Convenience: resolve title field id when only configuration id is known. */
  getTitleFieldIdByConfigurationId(configurationId: string): string | undefined {
    const templateId = this.getTemplateIdByConfigurationId(configurationId);
    if (!templateId) return undefined;
    return this.getTitleFieldIdForTemplate(templateId);
  }

  /** Try to resolve a human readable title value from a FormDto-like shape. */
  resolveTitleValue(configurationId: string, fieldsList: { formFieldDetailsId?: string; id?: string; value?: string }[]): string | undefined {
    if (!configurationId || !fieldsList?.length) return undefined;
    // Use basic lookup since we don't need full formFields, just the cached version
    const cfg = this.byConfigurationId.get(configurationId);
    if (!cfg || !cfg.formFields?.length) return undefined;
    let titleField = cfg.formFields?.find(f => f.label === 'Title');
    if (!titleField) titleField = cfg.formFields?.find(f => f.dataType?.toLowerCase() === 'string');
    if (!titleField) titleField = cfg.formFields?.[0];
    if (!titleField) return undefined;
    const val = fieldsList.find(v => v.formFieldDetailsId === titleField.id || v.id === titleField.id);
    return val?.value;
  }

  /** Fetch all configurations */
  private fetchAll(): Observable<PagedResultDto<FormConfigurationDto>> {
    const query = { maxResultCount: 1000 } as any;
    return this.configurationService.getList(query).pipe(
      tap(result => this.rebuildIndexes(result))
    );
  }

  private rebuildIndexes(result: PagedResultDto<FormConfigurationDto>) {
    this.byTemplate.clear();
    this.byConfigurationId.clear();
    this.cachedItems = result.items || [];
    
    for (const cfg of this.cachedItems) {
      if (!cfg) continue;
      const arr = this.byTemplate.get(cfg.formTemplateId) || [];
      arr.push(cfg);
      this.byTemplate.set(cfg.formTemplateId, arr);
      if (cfg.id) this.byConfigurationId.set(cfg.id, cfg);
    }
  }
}
